/**
 * The one place that shows the iOS App Tracking Transparency prompt.
 *
 * New members see it when they tap Continue on onboarding v3's "How it
 * works" screen (reason 'onboarding_how'). Everyone else (returning members,
 * legacy onboarding, members who signed in to an existing account) sees it
 * from NativeTrackingBootstrap once they are outside first-run onboarding
 * (reason 'fallback').
 *
 * NativeTrackingBootstrap registers the request handler, which owns the side
 * effects that follow a decision: consent state sync, RevenueCat attribution
 * sync, the app_tracking_consent_updated event and native_app_open on grant.
 * This module owns the gate: iOS native only, consent still pending, and at
 * most one prompt per app session.
 *
 * On the native side AppsFlyer waits up to 90 seconds for the ATT decision
 * (waitForATTUserAuthorization), and Meta's first activateApp is held the same
 * way, so a member who taps Allow here is still attributed.
 */
import {
  isNativeTrackingTransparencyRuntime,
  requestNativeTrackingPermission,
  type NativeTrackingAuthorizationStatus,
} from '@/utils/nativeTrackingTransparency';
import { getTrackingConsentStatus } from '@/utils/trackingManager';

export type TrackingConsentPromptReason = 'onboarding_how' | 'fallback';

export type TrackingConsentRequestHandler = (
  reason: TrackingConsentPromptReason
) => Promise<NativeTrackingAuthorizationStatus | null>;

export const IOS_ATT_PROMPTED_SESSION_KEY = 'jp_ios_att_prompted_session';

let requestHandler: TrackingConsentRequestHandler | null = null;
let inFlightRequest: Promise<void> | null = null;

function hasPromptedForTrackingThisSession(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(IOS_ATT_PROMPTED_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markTrackingPromptShownThisSession(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(IOS_ATT_PROMPTED_SESSION_KEY, '1');
  } catch {
    // Ignore sessionStorage failures.
  }
}

/**
 * NativeTrackingBootstrap calls this with the function that runs the native
 * request plus every side effect that depends on the signed-in member.
 * Returns an unregister function for effect cleanup.
 */
export function registerTrackingConsentRequestHandler(
  handler: TrackingConsentRequestHandler
): () => void {
  requestHandler = handler;
  return () => {
    if (requestHandler === handler) {
      requestHandler = null;
    }
  };
}

async function runRequest(reason: TrackingConsentPromptReason): Promise<void> {
  // The bootstrap is mounted app-wide on native, so the handler is normally
  // present. Without it, still show the prompt at the intended moment; the
  // bootstrap's next resume sync records the decision.
  const nativeStatus = requestHandler
    ? await requestHandler(reason)
    : await requestNativeTrackingPermission();

  // Only burn the once-per-session flag when the OS prompt actually displayed
  // and resolved. A request that comes back notDetermined never showed (app
  // was not active, or the alert was interrupted), so leave the flag unset and
  // let the next foreground retry it.
  if (nativeStatus && nativeStatus !== 'notDetermined') {
    markTrackingPromptShownThisSession();
  }
}

/**
 * Shows the iOS ATT prompt if this member has not decided yet and has not
 * already been prompted this session. A no-op on web and Android, when
 * consent is already granted or denied, and while a request is in flight.
 */
export async function requestTrackingConsentOnce(
  reason: TrackingConsentPromptReason
): Promise<void> {
  if (!isNativeTrackingTransparencyRuntime()) return;
  if (getTrackingConsentStatus() !== 'pending') return;
  if (hasPromptedForTrackingThisSession()) return;
  if (inFlightRequest) return inFlightRequest;

  inFlightRequest = runRequest(reason)
    .catch((error) => {
      console.error('Error requesting iOS tracking consent:', error);
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
}

/** Test-only reset for module state. */
export function resetTrackingConsentPromptForTests(): void {
  requestHandler = null;
  inFlightRequest = null;
}
